import { PLACEHOLDER_VALUE } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { Study } from '@/types';
import { Grid, GridItem } from '@nautilusbio/nb-design-system';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

interface StudyMetadataTableProps {
  study: Study;
  emptyText?: string;
}

interface MetadataRow {
  label: string;
  value: string;
  show?: boolean;
}

/**
 * Study metadata table component that displays created/updated information.
 * Uses the user's date format preference from the app store via useDateFormatter hook.
 */
export function StudyMetadataTable({ study, emptyText = PLACEHOLDER_VALUE }: StudyMetadataTableProps) {
  const { t } = useTranslation(['studies', 'general']);
  const formatDate = useDateFormatter();

  // Build metadata rows array
  const metadataRows: MetadataRow[] = useMemo(
    () => [
      {
        label: t('general:created'),
        value: formatDate(study?.created_at) || emptyText,
        show: true,
      },
      {
        label: t('general:created_by'),
        value: study?.created_by_full_name || emptyText,
        show: true,
      },
      {
        label: t('general:updated'),
        value: formatDate(study?.updated_at) || emptyText,
        show: !!study?.updated_at,
      },
      {
        label: t('general:updated_by'),
        value: study?.updated_by_full_name || emptyText,
        show: !!study?.updated_by_full_name,
      },
    ],
    [study, emptyText, formatDate, t],
  );

  return (
    <Grid className={styles.studyMetadataGrid} fullWidth gap={3}>
      {metadataRows
        .filter(row => row.show)
        .map((row, index) => (
          <Fragment key={`${row.label}-${index}`}>
            <GridItem className={styles.metadataLabel}>
              <Text as="span">{row.label}:</Text>
            </GridItem>
            <GridItem className={styles.metadataValue}>
              <Text as="strong">{row.value}</Text>
            </GridItem>
          </Fragment>
        ))}
    </Grid>
  );
}
